import * as React from 'react';
import { RadioListItem } from './RadioListItem';
import { IRadioProps } from './IRadioProps';
import styled from 'styled-components';
import { extractFormCellProps, FormCell, IFormCellProps } from '../FormCell';
import { JustifyContent } from '../../theming/types/JustifyContent';
import { IHasTestData } from '../../test-utilities/IHasTestData';
import { mergeTests } from '../../test-utilities/mergeTests';

export interface IRadioListProps extends IHasTestData {
  orientation: 'horizontal' | 'vertical';
  justifyContent?: JustifyContent;
}

const StyledWrapper = styled.div<IRadioListProps>`
  display: flex;
  flex-direction: ${(props) => (props.orientation === 'horizontal' ? 'row' : 'column')};
  justify-content: ${(props) =>
    props.justifyContent ? props.justifyContent : props.orientation === 'horizontal' ? 'space-between' : 'start'};
  align-items: flex-start;

  & > * {
    margin-left: ${(props) => (props.orientation === 'horizontal' ? props.theme.spacing.spacing5 : 0)};
    margin-top: ${(props) => (props.orientation === 'vertical' ? props.theme.spacing.spacing5 : 0)};
  }

  & > *:first-child {
    margin: 0;
  }
`;

/**
 * A radio input group that can have a single input checked at any one time.
 */
export const RadioList: React.FC<IRadioProps & IRadioListProps & IFormCellProps> = ({
  name,
  onChange,
  orientation,
  options,
  value: propsValue,
  justifyContent,
  autoFocus,
  ...rest
}) => {
  const { formCellProps } = extractFormCellProps(rest);
  const dataTestClass = rest['data-test-class'] || 'radio-list';
  return (
    <FormCell {...formCellProps} data-test-class={dataTestClass}>
      <StyledWrapper orientation={orientation} justifyContent={justifyContent}>
        {options.map(({ label, value, hintText }, i) => {
          const id = `${name}-${i}`;
          return (
            <RadioListItem
              checked={propsValue === value}
              data-test-id={mergeTests(rest['data-test-id'], id)}
              data-test-class={rest['data-test-class']}
              id={id}
              key={id}
              label={label}
              name={name}
              onChange={onChange}
              value={value}
              autoFocus={autoFocus ? propsValue === value : autoFocus}
              hintText={hintText}
            />
          );
        })}
      </StyledWrapper>
    </FormCell>
  );
};
